import { useCallback, useEffect, useState, useRef } from 'react';
import Socket from '../utils/Print/Socket';
import NMPrintSocket from '../utils/Print/Print';

interface PrinterInfo {
  name: string;
  port: string | number;
}

export const usePrinter = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [printers, setPrinters] = useState<PrinterInfo[]>([]);

  // Use refs to keep the instances alive across renders
  const socketRef = useRef<Socket | null>(null);
  const printerRef = useRef<NMPrintSocket | null>(null);

  useEffect(() => {
    // 1. Initialize Communication
    const socket = new Socket();
    const printer = new NMPrintSocket(socket);
    socketRef.current = socket;
    printerRef.current = printer;

    // 2. Open Connection
    socket.open((openBool) => {
      setIsConnected(openBool);
      if (openBool) {
        // eslint-disable-next-line no-console
        console.log('Printer Service Connected!');
      }
    });

    // Cleanup on unmount
    return () => {
      socket.close();
    };
  }, []);

  // Helper: Get USB Printers
  const getPrinters = useCallback(async (): Promise<PrinterInfo[]> => {
    if (!printerRef.current || !isConnected){
      setPrinters([]); // Clear printers if not connected
      return [];
    } 

    try {
      const res = await printerRef.current.getAllPrinters();
      if (res.resultAck.errorCode === 0) {
        const printerList = JSON.parse(res.resultAck.info);
        const list: PrinterInfo[] = Object.keys(printerList).map((key) => ({
          name: key,
          port: printerList[key],
        }));
        setPrinters(list);
        return list;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to get printers', e);
    }
    return [];
  }, [isConnected]);

  
  /**
   * Helper function to fetch image from URL and convert to base64
   */
  const fetchImageAsBase64 = useCallback(async (imageUrl: string): Promise<string> => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        // Remove the data:image/xxx;base64, prefix
        const base64Data = base64String.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  /**
   * Print label from image URL (e.g., from S3)
   * Use this when the barcode/QR code image is pre-generated from API
   * 
   * @param printerName - Printer name
   * @param port - Printer port
   * @param imageUrl - URL of the image to print (e.g., S3 signed URL)
   * @param options - Print options
   */
  const printImageFromUrl = useCallback(async (
    printerName: string,
    port: number,
    imageUrl: string,
    options: {
      width?: number;      // Label width in mm (default: 40)
      height?: number;     // Label height in mm (default: 20)
      imageWidth?: number; // Image width in mm (default: same as label width)
      imageHeight?: number; // Image height in mm (default: same as label height)
      x?: number;          // X position in mm (default: 0)
      y?: number;          // Y position in mm (default: 0)
      quantity?: number;   // Number of copies (default: 1)
      rotate?: number;     // Rotation: 0, 90, 180, 270 (default: 0)
    } = {}
  ) => {
    if (!printerRef.current || !isConnected) {
      // eslint-disable-next-line no-console
      console.error('Printer ref not initialized or not connected');
      return;
    }

    const printer = printerRef.current;
    // eslint-disable-next-line no-console
    console.log('printImageFromUrl options:', imageUrl ,options);
    const {
      width = 40,
      height = 20,
      imageWidth = width,
      imageHeight = height,
      x = 0,
      y = 0,
      quantity = 1,
      rotate = 0,
    } = options;

    try {
      // 1. Fetch and convert image to base64
      const imageBase64 = await fetchImageAsBase64(imageUrl);

      // 2. Connect to specific printer
      const selectRes = await printer.selectPrinter(printerName, port);
      if (selectRes?.resultAck?.errorCode !== 0) {
        // eslint-disable-next-line no-console
        console.error('Failed to select printer:', selectRes);
        return;
      }

      // 3. Initialize SDK
      await printer.initSdk({ fontDir: '' });

      // 4. Start Job
      const startRes = await printer.startJob(3, 1, 2, quantity);
      if (startRes?.resultAck?.errorCode !== 0) {
        // eslint-disable-next-line no-console
        console.error('Failed to start job:', startRes);
        return;
      }

      // 5. Setup Canvas
      const boardRes = await printer.InitDrawingBoard(
        JSON.stringify({
          width,
          height,
          rotate: 0,
          path: 'ZT001.ttf',
          verticalShift: 0,
          HorizontalShift: 0,
        })
      );
      if (boardRes?.resultAck?.errorCode !== 0) {
        // eslint-disable-next-line no-console
        console.error('Failed to init drawing board:', boardRes);
        return;
      }

      // 6. Draw the image
      const imageRes = await printer.DrawLabelImage(
        JSON.stringify({
          x,
          y,
          width: imageWidth,
          height: imageHeight,
          rotate,
          imageProcessingType: 0,
          imageProcessingValue: 127,
          imageData: imageBase64,
        })
      );
      // eslint-disable-next-line no-console
      console.log('DrawLabelImage response:', imageRes );

      // 7. Commit the job
      const commitRes = await printer.commitJob(
        undefined,
        JSON.stringify({
          printerImageProcessingInfo: { printQuantity: quantity },
        })
      );
      // eslint-disable-next-line no-console
      console.log('Image label printed successfully!', commitRes);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Print error:', error);
    }
  }, [isConnected, fetchImageAsBase64]);

 
  return {
    isConnected,
    printers,
    getPrinters,
    printImageFromUrl
  };
};
